import { ArrowRight, ChevronDown, Users } from "lucide-react";
import { toast } from "sonner";

import { GoalStatus } from "~/../drizzle/schema";
import { Button, ButtonRouter } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { GoalStatusBadge } from "~/components/ui/goal-badges";
import { Skeleton } from "~/components/ui/skeleton";
import { useDecryptedResource } from "~/lib/crypto";
import type { KeyManager } from "~/lib/crypto";
import type {
  GoalPrograms,
  ProgramGoalUnder,
} from "~/lib/queries/goalLinkQuery";
import { useLinkGoalToProgram } from "~/lib/queries/goalLinkQuery";

export type GoalLink = GoalPrograms["links"][number];

const programEnd = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
});

/** What removing the goal from a program keeps and what the coach loses. */
export function removeLinkConsequences(link: GoalLink): string {
  return `The goal and its steps stay in your Life Design. ${link.coachFirstName} can no longer see this goal or its steps. Goals ${link.coachFirstName} planned under it stay in the program.`;
}

/**
 * The programs a Life Design goal is in, and "Work on this with a coach" to
 * bring it into another of the client's active programs. A goal brought in is
 * a suggestion until the coach accepts it; the coach reads it and its steps
 * from the moment it is suggested.
 */
export function GoalProgramLinks({
  goalId,
  programs,
  keyManager,
  onCancelSuggestion,
}: {
  goalId: string;
  programs: GoalPrograms;
  keyManager: KeyManager | null;
  onCancelSuggestion: (link: GoalLink) => void;
}) {
  const linkGoal = useLinkGoalToProgram(keyManager);

  const bringInto = (program: GoalPrograms["programs"][number]) =>
    linkGoal.mutate(
      { goalId, engagementId: program.engagementId },
      {
        onSuccess: () => toast.success(`Suggested to ${program.coachName}`),
        onError: () =>
          toast.error("The goal was not shared with your coach. Try again."),
      },
    );

  if (programs.links.length === 0 && programs.programs.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {programs.links.map((link) =>
        link.status === "active" ? (
          <div
            key={link.engagementId}
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-purple-50 p-4 text-sm"
          >
            <div className="min-w-0 space-y-0.5">
              <p className="font-medium text-purple-900">
                In your program with {link.coachName}
              </p>
              <p className="text-purple-800">
                {link.coachFirstName} can see this goal and its steps.
              </p>
            </div>
            <ButtonRouter
              to="/coaching/$engagementId"
              params={{ engagementId: link.engagementId }}
              variant="link"
              size="sm"
              className="h-auto px-0"
            >
              Open program
              <ArrowRight aria-hidden />
            </ButtonRouter>
          </div>
        ) : (
          <div
            key={link.engagementId}
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-gray-50 p-4 text-sm"
          >
            <div className="min-w-0 space-y-0.5">
              <p className="font-medium text-gray-900">
                Suggested to {link.coachName}
              </p>
              <p className="text-text-secondary">
                {link.coachFirstName} can already read it. It joins the program
                when {link.coachFirstName} accepts.
              </p>
            </div>
            <Button
              variant="link"
              size="sm"
              className="h-auto px-0"
              onClick={() => onCancelSuggestion(link)}
            >
              Cancel
            </Button>
          </div>
        ),
      )}

      {programs.programs.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              disabled={!keyManager || linkGoal.isPending}
              loading={linkGoal.isPending}
            >
              <Users aria-hidden />
              {programs.links.length > 0
                ? "Work on this with another coach"
                : "Work on this with a coach"}
              <ChevronDown aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-w-xs">
            <DropdownMenuLabel className="eyebrow text-gray-500">
              Your active programs
            </DropdownMenuLabel>
            {programs.programs.map((program) => (
              <DropdownMenuItem
                key={program.engagementId}
                onSelect={() => bringInto(program)}
                className="flex-col items-start gap-0"
              >
                <span className="text-gray-900">{program.coachName}</span>
                <span className="text-text-tertiary text-xs">
                  Program ends {programEnd.format(new Date(program.dateEnd))}
                </span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <p className="text-text-secondary px-2 py-2 text-xs">
              Your coach will see this goal and its steps, and can plan program
              goals under it. You can remove it from the program at any time.
            </p>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function ProgramGoalRow({
  goal,
  keyManager,
}: {
  goal: ProgramGoalUnder;
  keyManager: KeyManager | null;
}) {
  const { data: decrypted, isDecrypting } = useDecryptedResource({
    resourceType: "goal",
    resourceId: goal.id,
    keyManager,
    fields: {
      description: {
        encrypted: goal.encryptedDescription,
        plaintext: goal.description,
      },
    },
  });
  return (
    <li className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2 text-sm">
      <span className="min-w-0 break-words text-gray-900">
        {isDecrypting ? (
          <Skeleton className="h-4 w-40" />
        ) : (
          (decrypted.description ?? goal.description ?? "Untitled")
        )}
      </span>
      {/* Program goals are open or done; their state is the program's. */}
      {goal.status === GoalStatus.COMPLETED && (
        <GoalStatusBadge status={goal.status} />
      )}
    </li>
  );
}

/**
 * The goals a coach planned under this Life Design goal. They are the
 * program's shared work, not steps: they do not tick here and do not count
 * toward the goal's steps.
 */
export function ProgramGoalsUnder({
  goals,
  keyManager,
}: {
  goals: readonly ProgramGoalUnder[];
  keyManager: KeyManager | null;
}) {
  if (goals.length === 0) return null;
  const coaches = [...new Set(goals.map((goal) => goal.coachFirstName))];
  return (
    <section className="space-y-3" aria-labelledby="goal-program-goals-heading">
      <div className="flex items-baseline justify-between gap-4">
        <h3
          id="goal-program-goals-heading"
          className="text-base font-medium text-gray-900"
        >
          In your program
        </h3>
        {coaches.length === 1 && coaches[0] && (
          <span className="text-text-tertiary text-sm">
            Goals {coaches[0]} planned with you
          </span>
        )}
      </div>
      <ul className="space-y-2">
        {goals.map((goal) => (
          <ProgramGoalRow key={goal.id} goal={goal} keyManager={keyManager} />
        ))}
      </ul>
    </section>
  );
}
